import { motion } from 'framer-motion';
import { Plus, X } from 'lucide-react';
import { rarityOf, SLOT_THEME, themeOf } from '../lib/classTheme';

export default function EquipSlot({ slot, item, classKey, onClick, onUnequip, size = 'md' }) {
  const s = SLOT_THEME[slot];
  const r = item ? rarityOf(item.rarity) : null;
  const t = themeOf(classKey);
  const dim = { sm: 'w-14 h-14', md: 'w-[68px] h-[68px]', lg: 'w-20 h-20' }[size];

  return (
    <div className="relative">
      <motion.button
        type="button"
        onClick={onClick}
        whileTap={{ scale: 0.93 }}
        className={`${dim} rounded-2xl border-2 overflow-hidden relative group transition-all
          ${item ? 'bg-white' : 'bg-ink-50 border-dashed'}`}
        style={item
          ? { borderColor: r.hex, boxShadow: r.shadowCss }
          : { borderColor: '#cbd5e1' }}
      >
        {item ? (
          <>
            <div className="absolute inset-0 opacity-10"
              style={{ background: `radial-gradient(circle at 50% 35%, ${r.hex}, transparent 70%)` }} />
            <img src={item.thumb || item.image} alt={item.name}
              className="relative w-full h-full object-cover" draggable={false} />

            {r.tier >= 4 && (
              <motion.div className="absolute inset-0 pointer-events-none"
                style={{ background: `linear-gradient(115deg, transparent 40%, ${r.hex}55 50%, transparent 60%)` }}
                animate={{ x: ['-100%', '100%'] }}
                transition={{ duration: 2.4, repeat: Infinity, repeatDelay: 2 }} />
            )}

            <div className="absolute inset-x-0 bottom-0 h-1" style={{ background: r.hex }} />
          </>
        ) : (
          <div className="w-full h-full grid place-items-center">
            <span className="text-xl opacity-25">{s.icon}</span>
            <Plus size={12} className="absolute bottom-1.5 right-1.5 text-ink-300" />
          </div>
        )}
      </motion.button>

      {item && onUnequip && (
        <button onClick={onUnequip}
          className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-ink-900 text-white
            grid place-items-center shadow-lg active:scale-90 transition opacity-0
            group-hover:opacity-100 sm:opacity-100">
          <X size={11} strokeWidth={3} />
        </button>
      )}

      <p className="text-[8px] font-black text-ink-400 uppercase text-center mt-1 tracking-wide">
        {s.label}
      </p>
    </div>
  );
}

/* ============================================================
   PAPER DOLL — hero + 5 slots around it
   ============================================================ */
export function EquipmentPanel({ hero, equipment, onSlotClick, onUnequip }) {
  const t = themeOf(hero?.class_key);
  const slots = equipment?.slots || {};

  return (
    <div className="card overflow-hidden">
      <div className="relative p-4" style={{ background: t.radial }}>
        <div className="flex items-center gap-4">
          {/* left column */}
          <div className="flex flex-col gap-3">
            <EquipSlot slot="helmet" item={slots.helmet} classKey={hero?.class_key}
              onClick={() => onSlotClick('helmet')}
              onUnequip={slots.helmet ? () => onUnequip('helmet') : null} />
            <EquipSlot slot="chest" item={slots.chest} classKey={hero?.class_key}
              onClick={() => onSlotClick('chest')}
              onUnequip={slots.chest ? () => onUnequip('chest') : null} />
            <EquipSlot slot="legs" item={slots.legs} classKey={hero?.class_key}
              onClick={() => onSlotClick('legs')}
              onUnequip={slots.legs ? () => onUnequip('legs') : null} />
          </div>

          {/* hero */}
          <div className="flex-1 relative">
            <motion.div className="relative rounded-2xl overflow-hidden border-2 mx-auto max-w-[150px]"
              style={{ borderColor: t.hex, boxShadow: t.glowCss }}
              animate={{ y: [0, -4, 0] }}
              transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}>
              {hero?.image_url ? (
                <img src={hero.image_url} alt={hero.name}
                  className="w-full aspect-[9/16] object-cover" />
              ) : (
                <div className="w-full aspect-[9/16] grid place-items-center bg-ink-100 text-3xl">
                  {t.icon}
                </div>
              )}
              <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-black/80 to-transparent" />
              <div className="absolute inset-x-0 bottom-0 p-2 text-white text-center">
                <p className="font-black text-[11px] truncate">{hero?.name}</p>
                <p className="text-[9px] opacity-80 font-bold">
                  L{hero?.level} · {equipment?.gearScore || 0} GS
                </p>
              </div>
            </motion.div>
          </div>

          {/* right column */}
          <div className="flex flex-col gap-3">
            <EquipSlot slot="weapon" item={slots.weapon} classKey={hero?.class_key}
              onClick={() => onSlotClick('weapon')}
              onUnequip={slots.weapon ? () => onUnequip('weapon') : null} />
            <EquipSlot slot="boots" item={slots.boots} classKey={hero?.class_key}
              onClick={() => onSlotClick('boots')}
              onUnequip={slots.boots ? () => onUnequip('boots') : null} />
            <div className="w-[68px] h-[68px] rounded-2xl border-2 border-dashed border-ink-200
              grid place-items-center opacity-40">
              <span className="text-[9px] font-black text-ink-400 text-center leading-tight">
                MORE<br />SOON
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* equipment stat totals */}
      {equipment?.totals && Object.values(equipment.totals).some(v => v > 0) && (
        <div className="p-3 border-t border-ink-100">
          <div className="flex items-center justify-between mb-2">
            <p className="label !mb-0">Equipment bonuses</p>
            <span className="chip-brand">{equipment.equippedCount}/5 slots</span>
          </div>
          <div className="flex flex-wrap gap-1">
            {Object.entries(equipment.totals)
              .filter(([, v]) => v > 0)
              .sort((a, b) => b[1] - a[1])
              .map(([k, v]) => (
                <span key={k} className="chip-mint">
                  +{v} {k.replace('_', '').slice(0, 4)}
                </span>
              ))}
          </div>
        </div>
      )}
    </div>
  );
}